// AuthModal - Login/Register Modal Component

import React, { useState } from 'react';
import { X, Mail, Lock, User, Eye, EyeOff, Link2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import type { AuthCredential } from '@firebase/auth';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'register';
}

export function AuthModal({ isOpen, onClose, initialMode = 'login' }: AuthModalProps) {
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // State for account linking flow
  const [linkingState, setLinkingState] = useState<{
    isLinking: boolean;
    email: string;
    pendingCredential: AuthCredential | null;
  }>({
    isLinking: false,
    email: '',
    pendingCredential: null,
  });

  const { signIn, signUp, signInWithGoogle, linkEmailAccount } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email, password);
      } else {
        if (!displayName.trim()) {
          setError('Bitte gib deinen Namen ein');
          setIsLoading(false);
          return;
        }
        await signUp(email, password, displayName);
      }
      onClose();
      resetForm();
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const result = await signInWithGoogle();

      if (result.success) {
        onClose();
        resetForm();
      } else if (result.needsLinking && result.email && result.pendingCredential) {
        // Account linking required - show linking UI
        setLinkingState({
          isLinking: true,
          email: result.email,
          pendingCredential: result.pendingCredential,
        });
        setEmail(result.email);
        setMode('login');
        setError(null);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkingState.pendingCredential) return;

    setError(null);
    setIsLoading(true);

    try {
      await linkEmailAccount(email, password, linkingState.pendingCredential);
      onClose();
      resetForm();
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelLinking = () => {
    setLinkingState({ isLinking: false, email: '', pendingCredential: null });
    setError(null);
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setError(null);
    setLinkingState({ isLinking: false, email: '', pendingCredential: null });
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
  };

  const getErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'code' in err) {
      const code = (err as { code: string }).code;
      switch (code) {
        case 'auth/email-already-in-use':
          return 'Diese E-Mail-Adresse wird bereits verwendet';
        case 'auth/invalid-email':
          return 'Ungültige E-Mail-Adresse';
        case 'auth/operation-not-allowed':
          return 'Anmeldung nicht erlaubt';
        case 'auth/weak-password':
          return 'Das Passwort muss mindestens 6 Zeichen haben';
        case 'auth/user-disabled':
          return 'Dieses Konto wurde deaktiviert';
        case 'auth/user-not-found':
          return 'Kein Konto mit dieser E-Mail gefunden';
        case 'auth/wrong-password':
          return 'Falsches Passwort';
        case 'auth/invalid-credential':
          return 'E-Mail oder Passwort ist falsch';
        case 'auth/too-many-requests':
          return 'Zu viele Versuche. Bitte versuche es später erneut';
        case 'auth/popup-closed-by-user':
          return 'Anmeldung abgebrochen';
        case 'auth/account-exists-with-different-credential':
          return 'Ein Konto mit dieser E-Mail existiert bereits. Bitte melde dich mit deinem Passwort an.';
        default:
          return 'Ein Fehler ist aufgetreten. Bitte versuche es erneut';
      }
    }
    return 'Ein Fehler ist aufgetreten';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {linkingState.isLinking
              ? 'Konten verknuepfen'
              : mode === 'login'
                ? 'Anmelden'
                : 'Registrieren'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Account Linking Info */}
          {linkingState.isLinking && (
            <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Link2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-indigo-700 mb-1">
                    Google-Konto verknuepfen
                  </p>
                  <p className="text-sm text-gray-600">
                    Fuer <strong>{linkingState.email}</strong> existiert bereits ein Konto mit Passwort.
                    Gib dein Passwort ein, um beide Anmeldemethoden zu verknuepfen.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Google Sign In - hide when linking */}
          {!linkingState.isLinking && (
            <>
              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Mit Google fortfahren
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">oder</span>
                </div>
              </div>
            </>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={linkingState.isLinking ? handleLinkSubmit : handleSubmit} className="space-y-4">
            {mode === 'register' && !linkingState.isLinking && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Dein Name"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.de"
                  className={`w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${linkingState.isLinking ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  required
                  readOnly={linkingState.isLinking}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Passwort
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-indigo-500 text-white font-semibold rounded-lg hover:bg-indigo-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Bitte warten...
                </>
              ) : linkingState.isLinking ? (
                <>
                  <Link2 className="w-5 h-5" />
                  Konten verknuepfen
                </>
              ) : mode === 'login' ? (
                'Anmelden'
              ) : (
                'Registrieren'
              )}
            </button>
          </form>

          {/* Switch Mode / Cancel Linking */}
          {linkingState.isLinking ? (
            <p className="mt-6 text-center">
              <button
                onClick={cancelLinking}
                className="text-sm text-gray-500 hover:text-gray-700 hover:underline"
              >
                Abbrechen
              </button>
            </p>
          ) : (
            <p className="mt-6 text-center text-sm text-gray-600">
              {mode === 'login' ? (
                <>
                  Noch kein Konto?{' '}
                  <button
                    onClick={switchMode}
                    className="text-indigo-600 font-semibold hover:underline"
                  >
                    Jetzt registrieren
                  </button>
                </>
              ) : (
                <>
                  Bereits ein Konto?{' '}
                  <button
                    onClick={switchMode}
                    className="text-indigo-600 font-semibold hover:underline"
                  >
                    Anmelden
                  </button>
                </>
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;
